import { useState, useEffect } from "react";
import { Activity, MousePointerClick, FileText, Clock } from "lucide-react";
import { api } from "../lib/api";

const fmt = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
};

/**
 * Analyse de navigation d'un compte (toutes les pages consultées sur le site).
 * userId : identifiant du compte. clicks : ouvertures de lien e-mail (optionnel).
 */
export const NavigationAnalysis = ({ userId, clicks = null }) => {
  const [nav, setNav] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!userId) { setNav(null); return; }
    setLoading(true);
    api.get(`/admin/navigation/${userId}`)
      .then((r) => setNav(r.data))
      .catch(() => setNav(null))
      .finally(() => setLoading(false));
  }, [userId]);

  const maxTop = nav?.top?.[0]?.count || 1;

  return (
    <div className="mt-6 rounded-xl border border-cyan/25 bg-cyan/5 p-5" data-testid="nav-analysis">
      <div className="mb-4 flex items-center gap-2 text-sm font-700 text-white">
        <Activity size={16} className="text-cyan" /> Analyse de navigation sur le site
      </div>

      {!userId ? (
        <p className="text-sm text-ink-secondary" data-testid="nav-analysis-empty">
          Cette personne n’a pas encore de compte / de connexion : aucune navigation à analyser pour l’instant.
          {clicks != null && <> Clics sur le lien e-mail : <span className="font-700 text-white">{clicks}</span>.</>}
        </p>
      ) : loading ? (
        <p className="text-sm text-ink-tertiary">Chargement…</p>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <div className="rounded-lg border border-white/10 bg-void p-3" data-testid="nav-total-pages">
              <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-ink-tertiary"><FileText size={12} /> Pages vues</div>
              <div className="mt-1 font-display text-2xl font-900 text-white">{nav?.total ?? 0}</div>
            </div>
            <div className="rounded-lg border border-white/10 bg-void p-3" data-testid="nav-unique-pages">
              <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-ink-tertiary"><Activity size={12} /> Pages uniques</div>
              <div className="mt-1 font-display text-2xl font-900 text-white">{nav?.unique_pages ?? 0}</div>
            </div>
            {clicks != null && (
              <div className="rounded-lg border border-white/10 bg-void p-3" data-testid="nav-clicks">
                <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-ink-tertiary"><MousePointerClick size={12} /> Clics e-mail</div>
                <div className="mt-1 font-display text-2xl font-900 text-white">{clicks}</div>
              </div>
            )}
            <div className="rounded-lg border border-white/10 bg-void p-3" data-testid="nav-last-seen">
              <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-ink-tertiary"><Clock size={12} /> Dernière visite</div>
              <div className="mt-1 text-xs font-600 text-white">{fmt(nav?.last_seen)}</div>
            </div>
          </div>

          {(nav?.total ?? 0) === 0 ? (
            <p className="mt-4 text-sm text-ink-secondary">Aucune page consultée pour l’instant.</p>
          ) : (
            <>
              <div className="mt-5">
                <div className="mb-2 text-[11px] uppercase tracking-[0.15em] text-ink-tertiary">Pages les plus consultées</div>
                <div className="space-y-2" data-testid="nav-top">
                  {(nav?.top || []).map((p, idx) => (
                    <div key={idx} className="flex items-center gap-3">
                      <span className="w-40 shrink-0 truncate font-mono text-xs text-white">{p.path}</span>
                      <div className="h-2 flex-1 overflow-hidden rounded-full bg-white/5">
                        <div className="h-full rounded-full bg-cyan" style={{ width: `${Math.max(6, (p.count / maxTop) * 100)}%` }} />
                      </div>
                      <span className="w-8 shrink-0 text-right text-xs tabular-nums text-ink-secondary">{p.count}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-5">
                <div className="mb-2 text-[11px] uppercase tracking-[0.15em] text-ink-tertiary">Navigation récente</div>
                <div className="max-h-56 space-y-1 overflow-y-auto rounded-lg border border-white/10 bg-void p-2" data-testid="nav-recent">
                  {(nav?.recent || []).map((e, idx) => (
                    <div key={idx} className="flex items-center justify-between gap-3 px-2 py-1.5 text-xs">
                      <span className="truncate font-mono text-ink-secondary">{e.path}</span>
                      <span className="shrink-0 text-ink-tertiary">{fmt(e.at)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};
